import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Shield, Lock, Users, Monitor, BarChart3, FileText, CheckCircle, ArrowRight } from 'lucide-react';
import { useLanguage } from '../../../context/LanguageContext';

const T = {
  heroLabel: { de: 'Beratungsfelder', en: 'Consulting Areas' },
  heroTitle1: { de: 'Ganzheitliche Lösungen', en: 'Holistic Solutions' },
  heroTitle2: { de: 'für den Mittelstand', en: 'for SMEs' },
  heroSub: { de: 'Umfassende Beratungsdienstleistungen in allen Bereichen der modernen Unternehmensführung.', en: 'Comprehensive consulting services in all areas of modern corporate management.' },
  scope: { de: 'Leistungsumfang', en: 'Scope of Services' },
  consultReq: { de: 'Beratung anfragen', en: 'Request Consultation' },
  ctaTitle: { de: 'Bereit für professionelle Beratung?', en: 'Ready for Professional Consulting?' },
  ctaSub: { de: 'Lassen Sie uns gemeinsam Ihre Herausforderungen analysieren und Lösungen entwickeln.', en: 'Let us analyse your challenges together and develop solutions.' },
  ctaBtn: { de: 'Erstgespräch vereinbaren', en: 'Schedule Initial Meeting' },
};

const SERVICES = {
  de: [
    { icon: Shield, title: 'Datenschutz & DSGVO', subtitle: 'Compliance & Sicherheit', desc: 'Vollumfängliche Beratung und Umsetzung aller datenschutzrechtlichen Anforderungen.', features: ['Datenschutz-Audits und Gap-Analysen', 'Verarbeitungsverzeichnis', 'Externer Datenschutzbeauftragter', 'Mitarbeiterschulungen', 'Datenschutz-Folgenabschätzungen', 'Betroffenenrechte-Management'], accent: '#3b82f6' },
    { icon: Lock, title: 'IT-Sicherheit', subtitle: 'Schutz & Prävention', desc: 'Maßgeschneiderte Sicherheitskonzepte zum Schutz vor Cyberangriffen und Datenverlusten.', features: ['Penetrationstests', 'Security-Awareness-Schulungen', 'Incident-Response-Pläne', 'Netzwerksicherheit', 'Anti-Phishing', 'ISO 27001 Begleitung'], accent: '#ef4444' },
    { icon: Users, title: 'HR-Beratung', subtitle: 'Personal & Entwicklung', desc: 'Strategische Personalberatung für Talentgewinnung, -entwicklung und -bindung.', features: ['Recruiting-Strategien', 'Führungskräfte-Coaching', 'Employer Branding', 'Arbeitsrecht-Beratung', 'Onboarding-Prozesse', 'Mitarbeiterbefragungen'], accent: '#22c55e' },
    { icon: Monitor, title: 'IT-Consulting', subtitle: 'Digitalisierung', desc: 'Begleitung bei der Modernisierung Ihrer IT-Landschaft und Geschäftsprozesse.', features: ['Digitalisierungsstrategie', 'Prozessautomatisierung', 'Cloud-Migration', 'Software-Auswahl (ERP, CRM)', 'IT-Modernisierung', 'Schnittstellen-Integration'], accent: '#8b5cf6' },
    { icon: BarChart3, title: 'Organisationsentwicklung', subtitle: 'Strukturen & Prozesse', desc: 'Strukturen und Prozesse optimieren für nachhaltiges Wachstum.', features: ['Prozessoptimierung', 'Change Management', 'Strategieberatung', 'Lean Management', 'Organisationsdesign', 'Workshop-Moderation'], accent: '#f59e0b' },
    { icon: FileText, title: 'Compliance', subtitle: 'Recht & Regulierung', desc: 'Rechtssichere Unternehmensführung in allen regulatorischen Bereichen.', features: ['Compliance-Management-Systeme', 'Risikomanagement', 'Whistleblower-Systeme (HinSchG)', 'Awareness-Programme', 'Interne Audits', 'Regulatorische Beratung'], accent: '#06b6d4' },
  ],
  en: [
    { icon: Shield, title: 'Data Protection & GDPR', subtitle: 'Compliance & Security', desc: 'Comprehensive consulting and implementation of all data protection requirements.', features: ['Data Protection Audits & Gap Analysis', 'Records of Processing', 'External Data Protection Officer', 'Employee Training', 'Data Protection Impact Assessments', 'Data Subject Rights Management'], accent: '#3b82f6' },
    { icon: Lock, title: 'IT Security', subtitle: 'Protection & Prevention', desc: 'Tailored security concepts to protect against cyber attacks and data loss.', features: ['Penetration Testing', 'Security Awareness Training', 'Incident Response Plans', 'Network Security', 'Anti-Phishing', 'ISO 27001 Guidance'], accent: '#ef4444' },
    { icon: Users, title: 'HR Consulting', subtitle: 'Personnel & Development', desc: 'Strategic HR consulting for talent acquisition, development and retention.', features: ['Recruiting Strategies', 'Leadership Coaching', 'Employer Branding', 'Employment Law Consulting', 'Onboarding Processes', 'Employee Surveys'], accent: '#22c55e' },
    { icon: Monitor, title: 'IT Consulting', subtitle: 'Digitalisation', desc: 'Support in modernising your IT landscape and business processes.', features: ['Digitalisation Strategy', 'Process Automation', 'Cloud Migration', 'Software Selection (ERP, CRM)', 'IT Modernisation', 'Interface Integration'], accent: '#8b5cf6' },
    { icon: BarChart3, title: 'Organisational Development', subtitle: 'Structures & Processes', desc: 'Optimising structures and processes for sustainable growth.', features: ['Process Optimisation', 'Change Management', 'Strategy Consulting', 'Lean Management', 'Organisation Design', 'Workshop Facilitation'], accent: '#f59e0b' },
    { icon: FileText, title: 'Compliance', subtitle: 'Law & Regulation', desc: 'Legally secure corporate governance in all regulatory areas.', features: ['Compliance Management Systems', 'Risk Management', 'Whistleblower Systems', 'Awareness Programmes', 'Internal Audits', 'Regulatory Consulting'], accent: '#06b6d4' },
  ],
};

export default function DSBBusinessLeistungen() {
  const { language } = useLanguage();
  const services = SERVICES[language];
  return (
    <main data-testid="sub-dsb-business-leistungen" className="bg-white" style={{ fontFamily: 'Manrope, sans-serif' }}>
      
      {/* Hero */}
      <section className="pt-32 pb-20 bg-gradient-to-b from-[#f8fafc] to-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div 
            initial={{ opacity: 0, y: 30 }} 
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <span className="text-[#F5A623] text-sm font-semibold tracking-[0.2em] uppercase mb-4 block">{T.heroLabel[language]}</span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#0F1A2E] mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {T.heroTitle1[language]}<br /><span className="text-[#F5A623]">{T.heroTitle2[language]}</span>
            </h1>
            <p className="text-xl text-[#475569] leading-relaxed">{T.heroSub[language]}</p>
          </motion.div>
        </div>
      </section>

      {/* Services */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="space-y-24">
            {services.map((service, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className={`grid lg:grid-cols-2 gap-16 items-center ${i % 2 === 1 ? 'lg:grid-flow-dense' : ''}`}
              >
                {/* Content */}
                <div className={i % 2 === 1 ? 'lg:col-start-2' : ''}>
                  <div 
                    className="w-16 h-16 rounded-2xl flex items-center justify-center mb-6"
                    style={{ backgroundColor: `${service.accent}15` }}
                  >
                    <service.icon size={32} style={{ color: service.accent }} />
                  </div>
                  
                  <span className="text-[#F5A623] text-xs font-semibold tracking-[0.15em] uppercase">{service.subtitle}</span>
                  <h2 className="text-3xl md:text-4xl font-bold text-[#0F1A2E] mt-2 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {service.title}
                  </h2>
                  <p className="text-lg text-[#475569] leading-relaxed mb-8">
                    {service.desc}
                  </p>
                  
                  <Link 
                    to="/dsb-business/kontakt"
                    className="inline-flex items-center gap-2 text-[#0F1A2E] hover:text-[#F5A623] transition-colors group"
                  >
                    <span className="text-sm font-semibold">{T.consultReq[language]}</span>
                    <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                  </Link>
                </div>
                
                {/* Features Card */}
                <div className={`bg-[#f8fafc] rounded-2xl p-8 md:p-10 border border-[#e2e8f0] ${i % 2 === 1 ? 'lg:col-start-1' : ''}`}>
                  <h3 className="text-lg font-semibold text-[#0F1A2E] mb-6">{T.scope[language]}</h3>
                  <div className="grid gap-4">
                    {service.features.map((feature, j) => (
                      <div key={j} className="flex items-center gap-3 bg-white rounded-lg p-4 border border-[#e2e8f0]">
                        <CheckCircle size={18} style={{ color: service.accent }} />
                        <span className="text-[#475569]">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-[#0F1A2E]">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>{T.ctaTitle[language]}</h2>
            <p className="text-lg text-white/70 mb-10">{T.ctaSub[language]}</p>
            <Link to="/dsb-business/kontakt" className="inline-flex items-center gap-3 bg-white text-[#0F1A2E] px-10 py-5 text-sm font-bold tracking-wider uppercase hover:bg-[#F5A623] hover:text-white transition-all">
              {T.ctaBtn[language]} <ArrowRight size={18} />
            </Link>
          </motion.div>
        </div>
      </section>
    </main>
  );
}
